import { ReactNode, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import OTPInput from "components/Auth/OTPInput";
import { OUTLINE_BUTTON_CLASS } from "components/FlowButtons";
import { CONTINUE_BUTTON_CLASS, FOCUS_RING_CLASS } from "components/FormV2";
import type { NewCodeOutcome } from "components/OtpVerification/ended-code";
import type { ConfirmSurface } from "pages/StartLead/events";

import {
  EmailLinkButton,
  SupportCallLine,
  TEXT_BUTTON_CLASS,
} from "./GrantParts";
import type { GrantMethod, SupportLineKind } from "./login-grant-flow";
import {
  destinationVariants,
  emailScreenLine,
  type CallLineEmphasis,
  type CodeInputState,
  type DestinationPhase,
  type EmailEmphasis,
  type ResendState,
} from "./verify-screen";

const codeSchema = z.object({
  code: z.string().length(6, "Please enter the complete verification code"),
});

/** The type of the lines under the title. */
const SUBHEADER_TYPE_CLASS = "text-base leading-[1.55] 2xs:text-lg";

/** The slot under the title that holds the number line. It is as tall as
 * the line on show needs, and no taller. */
export function DestinationSlot({ children }: { children: ReactNode }) {
  return <div className={SUBHEADER_TYPE_CLASS}>{children}</div>;
}

/** The line that says where the code or link is going, or, in the error tone,
 * that it is not. It always carries the destination.
 *
 * Every sentence the channel can say is laid in the one grid cell, and only
 * the one for `phase` is shown, so the line's height is the tallest sentence
 * at this width and a screen changing its sentence never moves what is below
 * it. With a `phase` of null none is shown; `overlay` is drawn in the cell
 * over them. */
export function DestinationLine({
  method,
  phase,
  destination,
  overlay,
}: {
  method: GrantMethod;
  phase: DestinationPhase | null;
  destination: string;
  overlay?: ReactNode;
}) {
  return (
    <div data-slot="destination" className="grid">
      {destinationVariants(method).map((variant) => {
        const shown = variant.phase === phase;
        const error = variant.copy.tone === "error";
        return (
          <p
            key={variant.phase}
            aria-hidden={shown ? undefined : true}
            role={shown && error ? "alert" : undefined}
            className={`col-start-1 row-start-1 ${shown ? "" : "invisible"} ${error ? "text-red-800" : "text-oat-700"}`}
          >
            {variant.copy.lead}
            <span className="whitespace-nowrap font-bold leading-none text-soil">
              {destination}
            </span>
            {variant.copy.tail}
          </p>
        );
      })}
      {overlay}
    </div>
  );
}

/** The recaptcha disclosure and rates line that stand in for the hidden
 * badge. */
export function SmsNotice() {
  return (
    <p className="text-xs text-oat-700">
      Standard SMS rates may apply. This site is protected by reCAPTCHA and the
      Google{" "}
      <a
        href="https://policies.google.com/privacy"
        target="_blank"
        rel="noopener noreferrer"
        className="underline"
      >
        Privacy Policy
      </a>{" "}
      and{" "}
      <a
        href="https://policies.google.com/terms"
        target="_blank"
        rel="noopener noreferrer"
        className="underline"
      >
        Terms of Service
      </a>{" "}
      apply.
    </p>
  );
}

type CallLine = {
  kind: SupportLineKind;
  surface: ConfirmSurface;
  emphasis: CallLineEmphasis;
};

function GoBackButton({
  method,
  onClick,
}: {
  method: GrantMethod;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className={TEXT_BUTTON_CLASS}>
      {method === "email_link"
        ? "Not your email? Go back"
        : "Not your number? Go back"}
    </button>
  );
}

export type TextVerifyBodyProps = {
  code: CodeInputState;
  /** The line under the code box; empty when nothing failed. */
  codeMessage: string;
  /** Said in the same line when nothing failed, such as that a new code was
   * texted; empty when there is nothing to say. */
  codeNotice: string;
  verifying: boolean;
  resend: ResendState;
  /** Resolves `new_code_sent` when the screen texted a replacement for a
   * code that ended: the box then clears and takes focus. */
  onSubmit: (code: string) => Promise<NewCodeOutcome>;
  onResend: () => void;
  /** Called when the visitor edits the code. */
  onCodeEdited: () => void;
  notice: ReactNode;
  /** The emailed-link button, or null where the grant does not offer one. */
  email: {
    emphasis: EmailEmphasis;
    sending: GrantMethod | null;
    error: string | null;
    onEmail: () => void;
  } | null;
  onChangeDestination?: () => void;
  call: CallLine;
};

/** The text layout below the number line. Every slot is on screen from the
 * first paint; the props only relabel, enable or restyle them. */
export function TextVerifyBody({
  code,
  codeMessage,
  codeNotice,
  verifying,
  resend,
  onSubmit,
  onResend,
  onCodeEdited,
  notice,
  email,
  onChangeDestination,
  call,
}: TextVerifyBodyProps) {
  const [codeComplete, setCodeComplete] = useState(false);
  const otpInputRef = useRef<{ focus: () => void } | null>(null);
  const methods = useForm({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: "" },
    mode: "onSubmit",
  });
  const busy = methods.formState.isSubmitting || verifying;
  const focusTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(focusTimer.current), []);

  const focusCodeInput = () => {
    window.clearTimeout(focusTimer.current);
    focusTimer.current = window.setTimeout(
      () => otpInputRef.current?.focus(),
      100,
    );
  };

  const submitting = useRef(false);
  const handleSubmit = methods.handleSubmit(async (data) => {
    if (data.code.length !== 6 || submitting.current) return;
    submitting.current = true;
    try {
      const outcome = await onSubmit(data.code);
      if (outcome === "new_code_sent") {
        methods.reset({ code: "" });
        setCodeComplete(false);
        focusCodeInput();
      }
    } finally {
      submitting.current = false;
    }
  });

  const handleComplete = (value: string) => {
    setCodeComplete(true);
    if (busy) return;
    methods.setValue("code", value);
    void handleSubmit();
  };

  const handleChange = (value: string) => {
    setCodeComplete(value.length === 6);
    onCodeEdited();
  };

  const handleResend = () => {
    methods.reset({ code: "" });
    setCodeComplete(false);
    onResend();
    focusCodeInput();
  };

  return (
    <div className="space-y-5">
      {notice}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit}>
          <div data-slot="code" className={busy ? "opacity-50" : ""}>
            <OTPInput
              name="code"
              control={methods.control}
              length={6}
              onComplete={handleComplete}
              onChange={handleChange}
              disabled={code.disabled || busy}
              busy={code.shimmer}
              error={codeMessage === "" ? null : codeMessage}
              showError={false}
              align="start"
              ref={otpInputRef}
            />
          </div>
          <p
            data-slot="code-message"
            aria-live="polite"
            className={`mt-2 min-h-5 text-sm ${codeMessage === "" ? "text-oat-700" : "text-red-800"}`}
          >
            {codeMessage === "" ? codeNotice : codeMessage}
          </p>
          <button
            type="submit"
            className={`${CONTINUE_BUTTON_CLASS} mt-2 w-full`}
            disabled={busy || !codeComplete}
          >
            {busy ? "Verifying..." : "Continue"}
          </button>
        </form>
      </FormProvider>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <button
          type="button"
          data-slot="resend"
          className={TEXT_BUTTON_CLASS}
          onClick={handleResend}
          disabled={resend.disabled}
        >
          {resend.label}
        </button>
        {onChangeDestination && (
          <GoBackButton method="sms_otp" onClick={onChangeDestination} />
        )}
      </div>
      {email && (
        <EmailLinkButton
          emphasis={email.emphasis}
          sending={email.sending}
          error={email.error}
          onEmail={email.onEmail}
        />
      )}
      <SupportCallLine
        kind={call.kind}
        surface={call.surface}
        screen="code"
        emphasis={call.emphasis}
      />
      <SmsNotice />
    </div>
  );
}

export type EmailVerifyBodyProps = {
  /** The latest send of the link failed. */
  sendFailed: boolean;
  resend: ResendState;
  onResend: () => void;
  notice: ReactNode;
  /** The text-instead button, or null where the grant offers no text. */
  text: {
    disabled: boolean;
    error: string | null;
    onText: () => void;
  } | null;
  onChangeDestination?: () => void;
  call: CallLine;
};

/** The emailed-link layout below the address line. */
export function EmailVerifyBody({
  sendFailed,
  resend,
  onResend,
  notice,
  text,
  onChangeDestination,
  call,
}: EmailVerifyBodyProps) {
  return (
    <div className="space-y-5">
      {notice}
      <p data-slot="email-line" className="text-base text-oat-700">
        {emailScreenLine(sendFailed)}
      </p>
      <button
        type="button"
        data-slot="resend"
        className={`${OUTLINE_BUTTON_CLASS} ${FOCUS_RING_CLASS} w-full`}
        onClick={onResend}
        disabled={resend.disabled}
      >
        {resend.label}
      </button>
      {text && (
        <div data-slot="text" className="space-y-2">
          <button
            type="button"
            className={TEXT_BUTTON_CLASS}
            onClick={text.onText}
            disabled={text.disabled}
          >
            Text me a code instead
          </button>
          {text.error !== null && (
            <p role="alert" className="text-sm text-red-800">
              {text.error}
            </p>
          )}
        </div>
      )}
      {onChangeDestination && (
        <div>
          <GoBackButton method="email_link" onClick={onChangeDestination} />
        </div>
      )}
      <SupportCallLine
        kind={call.kind}
        surface={call.surface}
        screen="code"
        emphasis={call.emphasis}
      />
    </div>
  );
}
